import { useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppContext from '../contexts/AppContext';
import DataContext from '../contexts/DataContext';
import ConfigContext from '../contexts/ConfigContext';
import TextInput from './Input';
import AnswerOptions from './AnswerOptions';
import Button from './Button';
import states from '../data/states';
import Autocomplete from 'react-google-autocomplete';
import { isMobile } from './../helpers';
import { getElecBillByState } from './BillSlider';

import satelliteImage from '../images/satellite-image.jpg';
import { appendScript } from '../helpers';

declare var google: any;

export default function PropertyAddress({ scrollToRef }: { scrollToRef: any }) {
  const {
    slides,
    currentSlide,
    goToNextSlide,
    updateSelectedOption,
    setChangedState,
    usedPanelOnTheRoofFailsafe,
    setUsedPanelOnTheRoofFailsafe,
    setEnphaseAccTariffs,
    setHasElecBillChanged,
    updateCurrentSlide,
    getSlideIndex,
    loadingRoofShade,
    setLoadingRoofShade,
    setUsedSolarAPI,
    usedSolarAPI,
    setSolarAPILotOfShade,
    setShowClickToCall,
    setIsISR,
    isISRState,
  } = useContext(AppContext);
  const { data, updateData } = useContext(DataContext);
  const configData = useContext(ConfigContext);
  const { features, updateConfigData, isDebrandedVersion } = configData;
  const [searchParams] = useSearchParams();
  const currentParams = Object.fromEntries(searchParams.entries());

  const [addressHaveError, setAddressHaveError] = useState(false);
  const [stateHaveError, setStateHaveError] = useState(false);
  const [zipHaveError, setZipHaveError] = useState(false);

  const sunlightSlideNumber = getSlideIndex('sunlight');
  const [roofShade, setRoofShade] = useState(
    slides[sunlightSlideNumber].selectedValue || 'Uncertain'
  );

  const [previousZipCode, setPreviousZipCode] = useState(data.selectedZipCode);

  const [googleAutocompleteActive, setGoogleAutocompleteActive] =
    useState(false);

  const findRoofShade = async (address: string) => {
    setRoofShade('Uncertain');
    const sunlightSlideNumber = getSlideIndex('sunlight');
    slides[sunlightSlideNumber].selectedValue = null;
    setUsedSolarAPI(false);
    setLoadingRoofShade(true);
    const geocoder = new google.maps.Geocoder();
    try {
      await geocoder.geocode(
        { address: address },
        async function (results: any, status: any) {
          if (status === 'OK') {
            const latLngShadeResponse = await fetch(
              `https://ztfs7b6dubqgheleincjlrqt540komou.lambda-url.us-east-1.on.aws/?lat=${results[0].geometry.location.lat()}&lng=${results[0].geometry.location.lng()}`
            );
            const latLngShade = await latLngShadeResponse.json();
            updateData({ ...data, googleSolarApiResponse: latLngShade });
            if (
              latLngShade.roofShade !== 'Uncertain' &&
              latLngShade.roofShade !== 'A Lot Of Shade'
            ) {
              setRoofShade(latLngShade.roofShade);
              const sunlightSlideNumber = getSlideIndex('sunlight');
              slides[sunlightSlideNumber].selectedValue = latLngShade.roofShade;
              setUsedSolarAPI(true);
              setSolarAPILotOfShade(false);
            } else if (latLngShade.roofShade === 'A Lot Of Shade') {
              setSolarAPILotOfShade(true);
            }
          }
          setLoadingRoofShade(false);
        }
      );
    } catch (e) {
      setLoadingRoofShade(false);
      setSolarAPILotOfShade(false);
    }
  };

  useEffect(() => {
    // Set default data
    // TODO: make this a generic/global hook
    let updatedData = {
      ...data,
      [slides[currentSlide].key]: slides[currentSlide].selectedValue,
    };

    updatedData = {
      ...updatedData,
      selectedState: slides[currentSlide].additionalFields.selectedState,
    };
    updatedData = {
      ...updatedData,
      selectedZipCode: slides[currentSlide].additionalFields.selectedZipCode,
    };
    updateData(updatedData);
  }, []);

  // const isValidZipCode = async (zipCode: String) => {
  //   const response = await fetch(
  //     `https://18kktizrg6.execute-api.us-east-1.amazonaws.com/staging/is-valid-zip-code?zipCode=${zipCode}`
  //   );
  //   const content = await response.json();
  //   return content.isValid;
  // };

  useEffect(() => {
    if (data.selectedZipCode && previousZipCode !== data.selectedZipCode) {
      if (features.useUtilityHeadline) {
        window.setGeoUtility = function (utilityResponse: any) {
          updateSelectedOption('provider', utilityResponse.utility || null);
          window.updateCity = function (response: any) {
            let selectedCity = response.data.placename || response.city;
            if (selectedCity) {
              updateData({
                ...data,
                selectedCity,
                utilityProvider: utilityResponse.utility || null,
              });
            } else {
              updateData({
                ...data,
                selectedCity: null,
                utilityProvider: utilityResponse.utility || null,
              });
            }
          };
          appendScript(
            `https://geoip.solarleadfactory.ai/zip2state/?callback=updateCity&zip=${data.selectedZipCode}`,
            true
          );
        };
        appendScript(
          `https://geoip.solarleadfactory.ai/utility2/?callback=setGeoUtility&zip=${data.selectedZipCode}`,
          true
        );
      }

      updateSelectedOption('provider', null);
      updateData({
        ...data,
        utilityProvider: null,
      });
      setEnphaseAccTariffs({});

      if (usedPanelOnTheRoofFailsafe) {
        updateConfigData({
          ...configData,
          features: { ...features, showPanelPlacement: true },
        });
        setUsedPanelOnTheRoofFailsafe(false);
      }

      setPreviousZipCode(data.selectedZipCode);
    }
  }, [data.selectedZipCode]);

  const [googleAddressHaveError, setGoogleAddressHaveError] = useState(false);
  const googleAutocompleteRef: any = useRef(null);

  const getGoogleAddressComponent = (
    types: string[],
    addressComponents: any
  ) => {
    return addressComponents.find((component: any) =>
      types.some((type: string) => component.types.includes(type))
    );
  };

  const getNameFromGoogleAddressComponents = (
    type: 'address' | 'city' | 'state' | 'zipCode',
    addressComponents: any
  ): string => {
    let result = '';
    switch (type) {
      case 'address':
        const streetNumberComp = getGoogleAddressComponent(
          ['street_number'],
          addressComponents
        );
        const streetName = getGoogleAddressComponent(
          ['route'],
          addressComponents
        );
        result = `${streetNumberComp ? streetNumberComp.long_name + ' ' : ''}${
          streetName.short_name
        }`;
        break;
      case 'city':
        const cityComp = getGoogleAddressComponent(
          [
            'locality',
            'sublocality',
            'sublocality_level_1',
            'sublocality_level_2',
            'administrative_area_level_2',
          ],
          addressComponents
        );
        if (cityComp) result = cityComp.long_name;
        break;
      case 'state':
        const stateComp = getGoogleAddressComponent(
          ['administrative_area_level_1'],
          addressComponents
        );
        result = stateComp.short_name;
        break;
      case 'zipCode':
        const zipCodeComp = getGoogleAddressComponent(
          ['postal_code'],
          addressComponents
        );
        result = zipCodeComp.long_name;
        break;
      default:
        break;
    }

    return result;
  };

  return (
    <div>
      {!!currentParams.test && currentParams.test === 'satellite' ? (
        <>
          <p className="mt-[30px] md:mt-10 text-[17px] md:text-[18px]">
            We will use satellite imagery to assess your roof's sunlight
            potential.
          </p>
          <img
            className="satelliteImage md:width-[100%]"
            src={satelliteImage}
            alt="Satellite of homes"
          />
        </>
      ) : (
        <>
          {!features.showSunnyBot && (
            <p className="mt-[30px] md:mt-10 text-[17px] short:!text-[16px] md:text-[18px]">
              Provide your address for estimate
            </p>
          )}
        </>
      )}
      {features.useGoogleAutocomplete && (
        <div className="inline-flex flex-col short:!w-[350px] md:w-[400px] w-full mt-3 md:mt-4 mb-6 md:mb-10 relative">
          <Autocomplete
            placeholder="123 Main St."
            defaultValue={data.googleFormattedAddress}
            ref={googleAutocompleteRef}
            apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
            onFocus={() => setGoogleAutocompleteActive(true)}
            onBlur={() => setGoogleAutocompleteActive(false)}
            onPlaceSelected={(place: any) => {
              if (!place) {
                setShowClickToCall(false);
                setIsISR(false);
                return;
              }

              if (googleAutocompleteRef && googleAutocompleteRef.current) {
                googleAutocompleteRef.current.value = place.formatted_address;
              }
              // Address have error if no zip code is returned
              const postalCodeComponent = getGoogleAddressComponent(
                ['postal_code'],
                place.address_components
              );
              if (!postalCodeComponent) {
                setGoogleAddressHaveError(true);
              } else {
                const address = getNameFromGoogleAddressComponents(
                  'address',
                  place.address_components
                );
                // Address have error if it doesn't start with a house number
                if (!address.match(/[0-9].+/)) {
                  setGoogleAddressHaveError(true);
                  return;
                }
                setGoogleAddressHaveError(false);

                if (features.noShadingQuestion) {
                  findRoofShade(place.formatted_address);
                }

                const city = getNameFromGoogleAddressComponents(
                  'city',
                  place.address_components
                );
                const state = getNameFromGoogleAddressComponents(
                  'state',
                  place.address_components
                );

                setShowClickToCall(isISRState(state));
                setIsISR(isISRState(state));
                // sessionStorage.setItem("modalCheckAdd", "true");

                const zipCode = getNameFromGoogleAddressComponents(
                  'zipCode',
                  place.address_components
                );
                updateSelectedOption(
                  slides[currentSlide].key,
                  address,
                  'selectedAddress'
                );
                updateSelectedOption(
                  slides[currentSlide].key,
                  state,
                  'selectedState'
                );
                updateSelectedOption(
                  slides[currentSlide].key,
                  zipCode,
                  'selectedZipCode'
                );
                updateSelectedOption('provider', null);
                updateData({
                  ...data,
                  selectedAddress: address,
                  selectedCity: city,
                  selectedState: state,
                  selectedZipCode: zipCode,
                  googleFormattedAddress: place.formatted_address,
                  utilityProvider: null,
                  elecBillByState: getElecBillByState(state),
                });
                setHasElecBillChanged(false);
              }

              if (features.showLanderConcept && isMobile()) {
                setTimeout(
                  scrollToRef?.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'center',
                  })
                );
              }
            }}
            className={`mt-3 md:mt-4 short:!w-[350px] md:w-[400px] w-full text-[3c3c3c] placeholder:font-normal md-max:max-w-[279px] h-12 md:h-[57px] border border-[#3c3c3c] rounded-[60px] text-[15px] short:text-[15px] md:text-[17px] py-[18.5px] px-6 ${
              googleAddressHaveError ? 'border-1 border-red-600' : ''
            }`}
            options={{
              types: ['address'],
              componentRestrictions: { country: 'us' },
            }}
            onChange={() => setGoogleAddressHaveError(true)}
          />
          {googleAddressHaveError && (
            <p className="text-[12px] short:!text-[13px] md:text-[14px] text-red-600 mt-2 pl-2">
              Please select your address from the drop-down list
            </p>
          )}
        </div>
      )}
      {!features.useGoogleAutocomplete && (
        <>
          <div>
            <TextInput
              name="address"
              placeholder="123 Main St."
              className="mt-3 md:mt-4 md:w-[400px]"
              errorMessage="Please enter a valid address."
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                const eventValue: string = e.currentTarget.value;
                const haveError = !eventValue || eventValue.trim().length < 1;
                const newValue = haveError ? null : eventValue;
                updateSelectedOption(
                  slides[currentSlide].key,
                  newValue,
                  'selectedAddress'
                );
                updateSelectedOption('provider', null);
                updateData({
                  ...data,
                  selectedAddress: newValue,
                  utilityProvider: null,
                });
                setAddressHaveError(haveError);
              }}
              haveError={addressHaveError}
              defaultValue={
                slides[currentSlide].additionalFields?.selectedAddress
              }
            />
          </div>
          <div className="inline-flex md:w-[400px] mt-3 md:mt-4 mb-6 md:mb-10">
            <TextInput
              name="state"
              placeholder="State"
              className="max-w-[112px] md:max-w-[167px] mr-[8px]"
              errorMessage="Please enter a valid state."
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                const eventValue: string = e.currentTarget.value.trim();
                const invalidChar = !!(
                  eventValue &&
                  eventValue.length &&
                  !eventValue[eventValue.length - 1].match(/[A-Za-z]/)
                );

                const haveError =
                  !eventValue ||
                  eventValue.length !== 2 ||
                  invalidChar ||
                  (eventValue.length === 2 &&
                    !Object.values(states).includes(eventValue.toUpperCase()));

                const newValue = invalidChar
                  ? slides[currentSlide].additionalFields?.selectedState
                  : eventValue;
                updateSelectedOption(
                  slides[currentSlide].key,
                  newValue,
                  'selectedState'
                );
                updateSelectedOption('provider', null);
                updateData({
                  ...data,
                  selectedState: newValue,
                  utilityProvider: null,
                });
                setStateHaveError(haveError);
                setChangedState(true);
              }}
              haveError={stateHaveError}
              value={slides[currentSlide].additionalFields?.selectedState}
              maxLength="2"
            />
            <TextInput
              name="zip"
              placeholder="Zip"
              className="max-w-[160px] md:max-w-[225px]"
              errorMessage="Please enter a valid zip code."
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                const eventValue: string = e.currentTarget.value.trim();
                const invalidChar = !!(
                  eventValue &&
                  eventValue.length &&
                  isNaN(Number(eventValue[eventValue.length - 1]))
                );

                const haveError =
                  !eventValue || eventValue.trim().length !== 5 || invalidChar;

                const newValue = invalidChar
                  ? slides[currentSlide].additionalFields?.selectedZipCode
                  : eventValue;
                updateSelectedOption(
                  slides[currentSlide].key,
                  newValue,
                  'selectedZipCode'
                );
                if (!haveError) {
                  updateData({ ...data, selectedZipCode: newValue });
                }
                setZipHaveError(haveError);
              }}
              haveError={zipHaveError}
              value={slides[currentSlide].additionalFields?.selectedZipCode}
              maxLength="5"
            />
          </div>
        </>
      )}
      <p className="mb-4 text-[17px] short:!text-[16px] md:text-[18px]">
        Do you own the property?
      </p>
      <div className="relative">
        <AnswerOptions
          options={slides[currentSlide].options}
          selectedOption={slides[currentSlide].selectedValue}
          updateSelectedOption={(newValue: any) => {
            updateSelectedOption('addressOwnership', newValue);
            updateData({ ...data, addressOwnership: newValue });
          }}
          slideKey={slides[currentSlide].key}
          className="relative"
        />

        {googleAutocompleteActive && (
          <div className="absolute top-0 left-0 w-full h-full"></div>
        )}
      </div>
      <div className="mt-[25px] md:mt-[40px]">
        <Button
          title="Continue"
          action={() => {
            if (slides[currentSlide].selectedValue === 'Rent') {
              window.location.href = `/${process.env
                .REACT_APP_ROOT_PATH!}?renter=yes`;
              return;
            }
            dataLayer.push({
              event: 'seform_address_entered',
              event_action: 'Do you own the property?',
              event_label: slides[currentSlide].selectedValue,
            });
            if (usedSolarAPI) {
              updateData({
                ...data,
                addressOwnership: slides[currentSlide].selectedValue,
                sunlight: roofShade,
              });
              // Skip shading question slide
              updateCurrentSlide(currentSlide + 2);
            } else {
              updateData({
                ...data,
                addressOwnership: slides[currentSlide].selectedValue,
              });
              goToNextSlide();
            }
          }}
          disabled={
            !slides[currentSlide].additionalFields?.selectedAddress ||
            !slides[currentSlide].additionalFields?.selectedState ||
            !slides[currentSlide].additionalFields?.selectedZipCode ||
            addressHaveError ||
            stateHaveError ||
            zipHaveError ||
            (features.useGoogleAutocomplete && googleAddressHaveError) ||
            loadingRoofShade
          }
          isLoading={loadingRoofShade}
        />
      </div>
    </div>
  );
}
